import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { serviceProductService } from '@/services/business/serviceProductService';

const REPORT_REASONS = [
  'Harassment',
  'Fraud or scam',
  'Spam',
  'Misinformation',
  'Hateful speech',
  'Threats or violence',
  'Self-harm',
  'Graphic content',
  'Dangerous or extremist organizations',
  'Sexual content',
  'Fake account',
  'Child exploitation',
  'Illegal goods and services',
  'Infringement',
];

interface Props {
  open: boolean;
  onClose: () => void;
  productId?: string | null;
  productTitle?: string;
}

export const ReportServiceProductDialog: React.FC<Props> = ({ open, onClose, productId, productTitle }) => {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setSelectedReason(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!productId || !selectedReason) {
      toast.error('Please select a reason for reporting');
      return;
    }
    try {
      setIsSubmitting(true);
      await serviceProductService.reportServiceProduct(productId, selectedReason);
      toast.success('Reported successfully');
      handleClose();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to report');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        Report{productTitle ? ` "${productTitle}"` : ''}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select your reporting reason
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {REPORT_REASONS.map((reason) => (
            <Chip
              key={reason}
              label={reason}
              onClick={() => setSelectedReason(reason)}
              color={selectedReason === reason ? 'primary' : 'default'}
              variant={selectedReason === reason ? 'filled' : 'outlined'}
              sx={{
                borderRadius: '16px',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: selectedReason === reason ? 'primary.dark' : 'action.hover',
                },
              }}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!selectedReason || isSubmitting}
          sx={{ borderRadius: '16px', px: 3 }}
        >
          {isSubmitting ? 'Reporting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
